<template>
  <v-dialog v-model="show"
            :width="1280"
            scrollable
            persistent
  >
    <v-sheet>
      <v-toolbar>
        <v-btn depressed color="primary" @click="generateReport">
          PDF로 다운로드
        </v-btn>

        <v-spacer />

        <v-toolbar-title>지원서</v-toolbar-title>

        <v-spacer />

        <v-btn icon @click="show = false">
          <v-icon large>
            $close
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text>
        <vue-html2pdf ref="html2Pdf"
                      :show-layout="true"
                      :float-layout="true"
                      :enable-download="true"
                      :preview-modal="false"
                      :filename="`${applicant.name || '지원자'}(${applicant.email || '이메일'})_지원서`"
                      :pdf-quality="2"
                      :manual-pagination="true"
                      pdf-format="a3"
                      pdf-orientation="portrait"
                      pdf-content-width="100%"
                      :margin="16"
        >
          <section slot="pdf-content" class="pdf-content v-application theme--light">
            <v-container class="mb-16">
              <v-toolbar flat>
                <v-spacer />
                <v-toolbar-title class="text-h4 font-weight-bold">
                  오렌즈 실무경험 지원서
                </v-toolbar-title>
                <v-spacer />
              </v-toolbar>

              <v-row>
                <v-col cols="2"
                       class="mt-2 mb-8"
                >
                  <v-row>
                    <v-spacer />
                    <v-col>
                      <v-sheet class="image-sheet">
                        <v-img width="113" height="151"
                               :aspect-ratio="113/151"
                               contain
                               class="mx-auto"
                               aria-controls
                               :src="computedApplicant.imagePath"
                        >
                          <div v-if="!computedApplicant.imagePath" style="width: 113px; height: 151px; border: 1px gray solid"
                               class="d-flex justify-center align-center text-center"
                          >
                            이미지 첨부<br>
                            (3x4)
                          </div>
                        </v-img>
                      </v-sheet>
                    </v-col>

                    <v-spacer />
                  </v-row>
                </v-col>

                <v-col cols="10">
                  <v-row class="mt-2">
                    <v-col v-for="(item, index) in applyFormSectionItems.personalInformation.inputs"
                           :key="`personalInformation-${index}`"
                           v-bind="item.colProps"
                    >
                      {{ item.label && `${item.label} : ` || '' }}{{ computedApplicant[item.inputName] || '-' }}
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row v-for="(item, index) in applyFormSections"
                     :key="`applyFormSections-${index}`"
                     class="mb-8"
              >
                <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].title"
                       cols="12"
                       class="px-0"
                >
                  <v-toolbar v-bind="toolbarProps">
                    <v-toolbar-title>{{ applyFormSectionItems[item] && applyFormSectionItems[item].title }}</v-toolbar-title>
                  </v-toolbar>
                </v-col>

                <v-col v-if="applyFormSectionItems[item].labels"
                       cols="12"
                >
                  <v-row>
                    <v-col v-for="(labelItem, labelIndex) in applyFormSectionItems[item].labels"
                           :key="`applyFormSectionItemsLabel-${labelIndex}`"
                           v-bind="labelItem.colProps"
                           class="grid-col-label"
                    >
                      {{ labelItem.label || '-' }}
                    </v-col>
                  </v-row>
                </v-col>

                <v-col v-for="(sectionInputs, subIndex) in applyFormSectionItems[item].inputs"
                       :key="`applyFormSectionItems-${subIndex}`"
                       v-bind="sectionInputs.colProps"
                       class="grid-col"
                >
                  {{ computedApplicant[sectionInputs.inputName] || '-' }}
                </v-col>
              </v-row>


              <v-toolbar flat class="mt-16 mb-2" style="page-break-before: always;">
                <v-spacer />
                <v-toolbar-title class="text-h4 font-weight-bold">
                  오렌즈 아카데미 자기소개서
                </v-toolbar-title>
                <v-spacer />
              </v-toolbar>

              <v-row v-for="(item, index) in applyFormIntroduceSections"
                     :key="`applyFormIntroduceSections-${index}`"
                     class="mb-8"
              >
                <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].title"
                       cols="12"
                       class="px-0"
                >
                  <v-toolbar v-bind="toolbarProps"
                             color="secondary"
                  >
                    <v-toolbar-title>{{ applyFormSectionItems[item] && applyFormSectionItems[item].title }}</v-toolbar-title>
                  </v-toolbar>
                </v-col>

                <v-col v-if="applyFormSectionItems[item] && applyFormSectionItems[item].description"
                       cols="12"
                       class="py-0"
                >
                  <v-alert v-bind="alertProps">
                    <div v-html="applyFormSectionItems[item] && applyFormSectionItems[item].description" />
                  </v-alert>
                </v-col>

                <v-col v-for="(sectionInputs, subIndex) in applyFormSectionItems[item].inputs"
                       :key="`applyFormSectionItems-${subIndex}`"
                       v-bind="sectionInputs.colProps"
                       class="grid-col"
                >
                  {{ computedApplicant[sectionInputs.inputName] || '-' }}
                </v-col>
              </v-row>
            </v-container>
          </section>
        </vue-html2pdf>
      </v-card-text>
    </v-sheet>
  </v-dialog>
</template>

<script>

import VueHtml2pdf from 'vue-html2pdf';
import { applyFormInfos } from '@/assets/infos/applyFormInfos';
import { toolbarProps, alertProps } from '@/assets/props/defaultProps';

export default {
  name: 'ApplicantViewDialog',
  components: {VueHtml2pdf},
  props: {
    value: {type: Boolean, default: false},
    applicant: {type: Object, default: () => ({})},
  },
  data: () => ({
    toolbarProps, alertProps,
  }),
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    applyFormSections: function() {
      return applyFormInfos.sections.filter(
        section => ['personalInformation'].some(checkSection => section !== checkSection),
      );
    },
    applyFormIntroduceSections: function() {
      return applyFormInfos.introduceSections;
    },
    applyFormSectionItems: function() {
      return applyFormInfos.sectionItems;
    },
    computedApplicant: function() {
      return this.applicant;
    },
  },
  methods: {
    generateReport() {
      this.$refs.html2Pdf.generatePdf();
    },
  },
};
</script>

<style scoped lang="scss">

.pdf-content {
  padding: 16px;

  > .container > .row {
    margin-top: 0;
  }


  .row {
    page-break-inside: avoid;
  }
}

::v-deep .layout-container {
  position: unset !important;
  width: 100% !important;
  height: unset !important;

  .content-wrapper {
    background-color: white;
  }
}

</style>